/** Navbar (components/Navbar/spec.md): the public bar and the session bar. */
import {useState} from 'react'
import {Form, Link, useRouteLoaderData} from 'react-router'
import classNames from 'classnames'
import {routerPaths} from '@routes'
import {Icon} from '@ui/Icon/icon'
import type {loader as rootLoader} from '@root'
import {homeFor} from '@product/quies/lib/hreflang'
import {useInterfaceStrings} from '@product/quies/i18n/catalogue'
import {plural} from '@product/quies/i18n/strings'
import {LanguagePicker} from '@product/quies/components/LanguagePicker/LanguagePicker'
import {VoicePicker} from '@product/quies/components/VoicePicker/VoicePicker'
import type {VoiceRoster} from '@product/quies/utils/voiceChoice'
import {usePopover} from '@product/quies/hooks/usePopover'
import {pickersEnabled, type NavbarState} from './navbarState'
import styles from './Navbar.module.scss'
import button from '@product/quies/styles/button.module.scss'

/** The account: a quiet mark in the bar, the session count only inside the menu — a practice is not run against a
 *  meter. A subscriber finds the billing portal here; anyone else, the prices. */
function AccountMenu({purchased = false}: {purchased?: boolean}) {
    const rootData = useRouteLoaderData<typeof rootLoader>('root')
    const {lang, t} = useInterfaceStrings()
    const {open, setOpen, containerRef, triggerRef, popoverRef} = usePopover()

    if (!rootData) return null
    const {user, tokens, hasPlan} = rootData

    if (!user) {
        return (
            <Link to="/auth/login" className={button.quiet}>
                {t.nav.signIn}
            </Link>
        )
    }

    return (
        <div ref={containerRef} className={styles.account}>
            <button
                ref={triggerRef}
                type="button"
                className={classNames(styles.accountTrigger, {[styles.purchased]: purchased})}
                aria-haspopup="menu"
                aria-expanded={open}
                aria-label={t.nav.account}
                onClick={() => setOpen((o) => !o)}
            >
                <Icon id="qs-account" size={18} outline aria-hidden="true" />
            </button>
            {open && (
                <div ref={popoverRef} className={styles.accountMenu} role="menu">
                    <p className={styles.balance}>
                        <Icon id="qs-sessions" size={16} outline aria-hidden="true" />
                        {plural(t.nav.sessions, lang, tokens ?? 0)}
                    </p>
                    {hasPlan ? (
                        <Link to={routerPaths['/stripe-portal']} reloadDocument role="menuitem" className={styles.menuItem}>
                            <Icon id="qs-billing" size={16} outline />
                            {t.pricing.manageSubscription}
                        </Link>
                    ) : (
                        <Link to={{pathname: homeFor(lang), hash: '#pricing'}} role="menuitem" className={styles.menuItem} onClick={() => setOpen(false)}>
                            <Icon id="qs-billing" size={16} outline />
                            {t.nav.viewPricing}
                        </Link>
                    )}
                    <Form action={routerPaths['/auth/logout']} method="POST">
                        <button type="submit" role="menuitem" className={styles.menuItem}>
                            <Icon id="qs-sign-out" size={16} outline />
                            {t.nav.signOut}
                        </button>
                    </Form>
                </div>
            )}
        </div>
    )
}

type NavbarProps =
    | {variant: 'public'}
    | {
          variant: 'session'
          navbarState: NavbarState
          language: string | null
          voiceId: string | null
          onLanguageChange: (code: string) => void
          onVoiceChange: (voiceId: string) => void
          /** The live voice catalogue (session loader). */
          voiceRoster: VoiceRoster
          /** A checkout just returned with success: the count's border holds sage. */
          purchased?: boolean
      }

export function Navbar(props: NavbarProps) {
    const {lang, t} = useInterfaceStrings()
    const l = t.nav
    const home = homeFor(lang)
    const [menuOpen, setMenuOpen] = useState(false)

    if (props.variant === 'session') {
        const {navbarState, language, voiceId, onLanguageChange, onVoiceChange, voiceRoster, purchased} = props
        const receded = navbarState === 'receded'
        const hidden = navbarState === 'hidden'
        const disabled = !pickersEnabled(navbarState)

        return (
            <header
                className={classNames(styles.navbar, styles.session, {[styles.receded]: receded, [styles.hidden]: hidden})}
                inert={hidden}
            >
                <Link to={home} className={styles.wordmark}>
                    Quies
                </Link>
                <div className={styles.sessionControls}>
                    {navbarState !== 'first' && (
                        <>
                            <LanguagePicker currentLanguage={language} onLanguageChange={onLanguageChange} disabled={disabled} />
                            <VoicePicker currentLanguage={language} currentVoiceId={voiceId} onVoiceChange={onVoiceChange} voiceRoster={voiceRoster} disabled={disabled} />
                        </>
                    )}
                    <AccountMenu purchased={purchased} />
                </div>
            </header>
        )
    }

    return (
        <header className={styles.navbar}>
            <Link to={home} className={styles.wordmark}>
                Quies
            </Link>
            <nav aria-label={l.mainNav} className={styles.links}>
                <ul>
                    <li><Link to={{pathname: home, hash: '#faq'}}>{l.faq}</Link></li>
                    <li><Link to={{pathname: home, hash: '#pricing'}}>{l.pricing}</Link></li>
                    <li><Link to={routerPaths['/legal']}>{l.legal}</Link></li>
                </ul>
            </nav>
            <div className={styles.publicActions}>
                <div className={styles.desktopAccount}><AccountMenu /></div>
                <Link to={routerPaths['/session']} className={classNames(button.primary, button.compact)}>
                    {l.beginSession}
                </Link>
                <button
                    type="button"
                    className={styles.menuToggle}
                    aria-label={menuOpen ? l.closeMenu : l.menu}
                    aria-expanded={menuOpen}
                    onClick={() => setMenuOpen((o) => !o)}
                >
                    <Icon id={menuOpen ? 'qs-close' : 'qs-menu'} size={20} outline />
                </button>
            </div>
            {menuOpen && (
                <div className={styles.menuSheet}>
                    <Link to={{pathname: home, hash: '#faq'}} onClick={() => setMenuOpen(false)}>{l.faq}</Link>
                    <Link to={{pathname: home, hash: '#pricing'}} onClick={() => setMenuOpen(false)}>{l.pricing}</Link>
                    <Link to={routerPaths['/legal']} onClick={() => setMenuOpen(false)}>{l.legal}</Link>
                    <div className={styles.sheetSeparator} />
                    <AccountMenu />
                </div>
            )}
        </header>
    )
}
